import { useCallback, useEffect, useRef } from 'react';
import {
  DragStartEvent,
  DragMoveEvent,
  DragEndEvent,
  DragCancelEvent,
  PointerSensor,
  useSensor,
  useSensors,
  type Modifier
} from '@dnd-kit/core';
import { ElementLanes, type ElementResizeSide } from '../types';
import { VerticalDragState } from '../../hooks/useVerticalDrag';
import { zoomLevelStore } from '../../store/zoomLevel';
import { ElementResizeSideEnum } from '../constants';
import { getAbsolutePlayerTime } from '../../timeUtils';
import {
  updateElementFrames,
  updateElementLanesOnHorizontalDrag,
  moveElementToLane,
  updatePlayerConfigWithElementLanes,
  getElementDuration,
  isElementLooping
} from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { CompositionConfig } from '@/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { Clip } from '@/player-config/src/types/clip-types';

const VERTICAL_DRAG_THRESHOLD = 15;
const LANE_HEIGHT = 30;
const LANE_GAP = 4;
const VERTICAL_BOUNDARY_OFFSET = 15;

// Snap within 8 viewport pixels of another element's edge
const SNAP_THRESHOLD_PX = 8;

export interface DragItemData {
  elementId: string;
  side: ElementResizeSide;
  laneIndex: number;
}

interface UseTimelineElementDnDProps {
  fps: number;
  isOverlayLayeringEnabled: boolean;
  playerConfig: CompositionConfig | null;
  clipData: Clip;
  clipId: string;
  elementLanes: ElementLanes;
  lanesContainerRef: React.RefObject<HTMLDivElement>;
  elementLanesRef: React.MutableRefObject<ElementLanes | null>;
  initialFramesRef: React.MutableRefObject<{ fromFrame: number; toFrame: number } | null>;
  isUpdatingFromVerticalDragRef: React.MutableRefObject<boolean>;
  setDraggingElementId: (id: string | null) => void;
  setElementLanes: React.Dispatch<React.SetStateAction<ElementLanes>>;
  setIsVerticalDragging: React.Dispatch<React.SetStateAction<boolean>>;
  setVerticalDragState: React.Dispatch<React.SetStateAction<VerticalDragState | null>>;
  resetDragState: () => void;
}

export const useTimelineElementDnD = ({
  fps,
  isOverlayLayeringEnabled,
  playerConfig,
  clipData,
  clipId,
  elementLanes,
  lanesContainerRef,
  elementLanesRef,
  initialFramesRef,
  isUpdatingFromVerticalDragRef,
  setDraggingElementId,
  setElementLanes,
  setIsVerticalDragging,
  setVerticalDragState,
  resetDragState
}: UseTimelineElementDnDProps) => {
  const isVerticalDragModeRef = useRef(false);
  const lastVerticalStateRef = useRef<{
    targetLaneIndex: number | null;
    snapPosition: 'top' | 'bottom' | null;
  }>({ targetLaneIndex: null, snapPosition: null });

  // Keeps the latest elementLanes accessible inside the modifier without
  // recreating it on every state update.
  const elementLanesSnapshotRef = useRef<ElementLanes>(elementLanes);
  useEffect(() => {
    elementLanesSnapshotRef.current = elementLanes;
  }, [elementLanes]);

  // Stores the snapped horizontal delta so handleDragEnd can commit the right
  // position. event.delta.x is the raw pointer delta and ignores modifiers.
  const snappedDeltaXRef = useRef(0);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 3 }
    })
  );

  // ─── Snap modifier ────────────────────────────────────────────────────────
  // dnd-kit calls this on every pointermove and applies the returned transform
  // to useDraggable's `transform` value. Snapping here means the visual jumps
  // to the snapped position with zero React state updates.
  const snapModifier: Modifier = useCallback(
    ({ transform, active }) => {
      if (!active) {
        return transform;
      }

      const data = active.data.current as DragItemData | undefined;
      if (!data) {
        return transform;
      }

      const { elementId, side } = data;
      const lanes = elementLanesSnapshotRef.current;
      const currentElement = lanes.flat().find(el => el.id === elementId);
      if (!currentElement) {
        return transform;
      }

      const { zoomLevel } = zoomLevelStore.getSnapshot();

      // Convert a frame number to its viewport-pixel position (same formula
      // used by the element's CSS left/width, so snap aligns with the visual).
      const frameToPx = (frame: number) => getAbsolutePlayerTime(frame / fps, clipData) * fps * zoomLevel;

      // Collect every other element's start and end edges as snap targets.
      const snapPositions: number[] = [];
      for (const lane of lanes) {
        for (const el of lane) {
          if (el.id === elementId) {
            continue;
          }
          snapPositions.push(frameToPx(el.fromFrame), frameToPx(el.toFrame));
        }
      }

      let finalX = transform.x;

      if (snapPositions.length > 0) {
        const fromPx = frameToPx(currentElement.fromFrame);
        const toPx = frameToPx(currentElement.toFrame);

        let bestSnapX = transform.x;
        let bestDist = SNAP_THRESHOLD_PX;

        // Try snapping a given edge (currentEdge = its position after transform)
        // to any snap target. Updates bestSnapX and bestDist in place.
        const trySnap = (currentEdgePx: number, originalEdgePx: number) => {
          for (const pos of snapPositions) {
            const dist = Math.abs(currentEdgePx - pos);
            if (dist < bestDist) {
              bestDist = dist;
              // Adjust transform.x so the edge lands exactly on the snap target
              bestSnapX = pos - originalEdgePx;
            }
          }
        };

        if (side === ElementResizeSideEnum.START) {
          // Only the left edge moves during a start-resize
          trySnap(fromPx + transform.x, fromPx);
        } else if (side === ElementResizeSideEnum.END) {
          // Only the right edge moves during an end-resize
          trySnap(toPx + transform.x, toPx);
        } else {
          // Center move: check both edges; nearest wins
          trySnap(fromPx + transform.x, fromPx);
          trySnap(toPx + transform.x, toPx);
        }

        finalX = bestSnapX;
      }

      // For audio/video elements, prevent start/end drags from visually exceeding
      // the native media duration (non-looping). The remaining frames available
      // determines the maximum px the handle can travel in the expanding direction.
      if (side !== ElementResizeSideEnum.CENTER) {
        const nativeDurationSecs = getElementDuration(currentElement);
        const looping = isElementLooping(currentElement);
        if (nativeDurationSecs > 0 && !looping) {
          const maxDurationFrames = nativeDurationSecs * fps;
          const currentDurationFrames = currentElement.toFrame - currentElement.fromFrame;
          const remainingFrames = maxDurationFrames - currentDurationFrames;

          if (side === ElementResizeSideEnum.START) {
            // Negative x drags the start handle left, increasing duration
            finalX = Math.max(finalX, -remainingFrames * zoomLevel);
          } else {
            // Positive x drags the end handle right, increasing duration
            finalX = Math.min(finalX, remainingFrames * zoomLevel);
          }
        }
      }

      snappedDeltaXRef.current = finalX;
      return { ...transform, x: finalX };
    },
    // elementLanesSnapshotRef is a ref, so it doesn't need to be a dep.
    // fps and clipData are the true inputs that can change between renders.
    [fps, clipData]
  );

  // Returns the target lane and snap-line direction based purely on how far the
  // element has moved vertically.  The snap line only appears once the element
  // crosses 50% of a lane height (~15 px), giving discrete "magnetic" steps
  // instead of continuously tracking the cursor position within a lane.
  const computeSnapTarget = useCallback(
    (
      verticalOffset: number,
      currentLaneIndex: number
    ): { targetLaneIndex: number; snapPosition: 'top' | 'bottom' } | null => {
      if (!lanesContainerRef.current) {
        return null;
      }

      const TOTAL_LANE = LANE_HEIGHT + LANE_GAP;
      const HALF_LANE = LANE_HEIGHT / 2;
      const absOffset = Math.abs(verticalOffset);

      if (absOffset < HALF_LANE) {
        return null;
      }

      const rect = lanesContainerRef.current.getBoundingClientRect();
      const maxLanes = Math.floor((rect.height + LANE_GAP) / TOTAL_LANE);
      const direction = verticalOffset > 0 ? 1 : -1;
      const fullLanes = Math.floor(absOffset / TOTAL_LANE);
      const remainder = absOffset % TOTAL_LANE;
      const lanesDelta = (fullLanes + (remainder >= HALF_LANE ? 1 : 0)) * direction;

      if (lanesDelta > 0) {
        // Moving down: allow targeting one lane beyond the last existing lane so
        // moveElementToLane can push a brand-new lane (handles single-track case).
        const targetLaneIndex = Math.min(maxLanes, currentLaneIndex + lanesDelta);
        if (targetLaneIndex === currentLaneIndex) {
          return null;
        }
        // Snap line sits in the gap above the target lane (between the two tracks).
        return { targetLaneIndex, snapPosition: 'top' };
      }

      // Moving up: clamp to lane 0.  When the element is already in lane 0,
      // snapPosition 'top' tells moveElementToLane to splice a new lane before
      // index 0 (triggered when there is time overlap with existing elements).
      const targetLaneIndex = Math.max(0, currentLaneIndex + lanesDelta);
      if (targetLaneIndex === currentLaneIndex && currentLaneIndex > 0) {
        return null;
      }
      // Snap line: 'top' of lane 0 (y = 0) when already at the top, otherwise
      // at the bottom edge of the target lane (in the gap between tracks).
      return {
        targetLaneIndex,
        snapPosition: targetLaneIndex === 0 && currentLaneIndex === 0 ? 'top' : 'bottom'
      };
    },
    [lanesContainerRef]
  );

  const calculateClampedVerticalOffset = useCallback(
    (rawOffset: number, initialLaneIndex: number): number => {
      if (!lanesContainerRef.current) {
        return rawOffset;
      }
      const rect = lanesContainerRef.current.getBoundingClientRect();
      const totalLaneHeight = LANE_HEIGHT + LANE_GAP;
      const maxLanes = Math.floor((rect.height + LANE_GAP) / totalLaneHeight);
      const minOffset = -(initialLaneIndex * totalLaneHeight) - VERTICAL_BOUNDARY_OFFSET;
      // Allow one extra lane of visual movement downward so the element can visually
      // travel into a new lane that doesn't exist yet (single-track scenario).
      const maxOffset = (maxLanes - initialLaneIndex) * totalLaneHeight + VERTICAL_BOUNDARY_OFFSET;
      return Math.max(minOffset, Math.min(maxOffset, rawOffset));
    },
    [lanesContainerRef]
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { elementId } = event.active.data.current as DragItemData;
      isVerticalDragModeRef.current = false;
      initialFramesRef.current = null;
      lastVerticalStateRef.current = { targetLaneIndex: null, snapPosition: null };
      snappedDeltaXRef.current = 0;
      setDraggingElementId(elementId);
      document.body.style.cursor = 'ew-resize';
      EventBus.dispatch(CustomEvents.NewTimelineEntityDraggingStateChanged, { isDraggingTimelineEntity: true });
    },
    [initialFramesRef, setDraggingElementId]
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const data = event.active.data.current as DragItemData | undefined;
      if (!data) {
        return;
      }

      const { side, laneIndex } = data;
      const { delta } = event;
      const absY = Math.abs(delta.y);

      // Vertical lane-change activates once the Y threshold is crossed on a center drag.
      // No ratio restriction — diagonal movement (any combination of X and Y) is allowed.
      const canBeVertical =
        side === ElementResizeSideEnum.CENTER && isOverlayLayeringEnabled && !isVerticalDragModeRef.current;

      if (canBeVertical && absY >= VERTICAL_DRAG_THRESHOLD) {
        isVerticalDragModeRef.current = true;
        setIsVerticalDragging(true);
        document.body.style.cursor = 'move';
      }

      if (isVerticalDragModeRef.current) {
        const clampedOffset = calculateClampedVerticalOffset(delta.y, laneIndex);
        const snapTarget = computeSnapTarget(delta.y, laneIndex);

        lastVerticalStateRef.current = {
          targetLaneIndex: snapTarget?.targetLaneIndex ?? null,
          snapPosition: snapTarget?.snapPosition ?? null
        };
        setVerticalDragState({
          isVerticalDragging: true,
          verticalOffset: clampedOffset,
          targetLaneIndex: snapTarget?.targetLaneIndex ?? null,
          snapPosition: snapTarget?.snapPosition ?? null
        });
        // No return — horizontal visual feedback (via useDraggable transform) runs in parallel.
      }

      // Horizontal drag: no state update here.
      // The snap modifier adjusts transform.x and writes snappedDeltaXRef.
    },
    [
      isOverlayLayeringEnabled,
      setIsVerticalDragging,
      setVerticalDragState,
      computeSnapTarget,
      calculateClampedVerticalOffset
    ]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      document.body.style.cursor = '';
      EventBus.dispatch(CustomEvents.NewTimelineEntityDraggingStateChanged, { isDraggingTimelineEntity: false });

      const data = event.active.data.current as DragItemData | undefined;

      // Use snappedDeltaXRef instead of event.delta.x — the modifier adjusts the
      // visual transform but event.delta always carries the raw pointer displacement.
      const { zoomLevel } = zoomLevelStore.getSnapshot();
      const timeOffset = snappedDeltaXRef.current / zoomLevel / fps;

      if (isVerticalDragModeRef.current) {
        // Vertical (or diagonal) drag: move to target lane, then apply horizontal offset.
        const { targetLaneIndex, snapPosition } = lastVerticalStateRef.current;
        const elementId = data?.elementId;

        if (targetLaneIndex !== null && snapPosition && elementId) {
          isUpdatingFromVerticalDragRef.current = true;

          setElementLanes(currentLanes => {
            let updatedLanes = moveElementToLane(currentLanes, elementId, targetLaneIndex, snapPosition);

            const movedElement = updatedLanes.flat().find(el => el.id === elementId);
            if (movedElement && timeOffset !== 0) {
              const updatedElement = updateElementFrames({
                element: movedElement,
                dragOffset: timeOffset,
                dragSide: ElementResizeSideEnum.CENTER,
                initialFrames: { fromFrame: movedElement.fromFrame, toFrame: movedElement.toFrame },
                fps
              });
              updatedLanes = updateElementLanesOnHorizontalDrag(updatedLanes, updatedElement);
            }

            elementLanesRef.current = updatedLanes;
            if (playerConfig) {
              updatePlayerConfigWithElementLanes(playerConfig, updatedLanes, clipData, clipId);
            }
            return updatedLanes;
          });
        } else if (elementId && timeOffset !== 0) {
          // Vertical drag was activated but the release position did not cross the lane-change
          // threshold (user pulled back before releasing, or element is already at the boundary).
          // Still commit the horizontal movement in the original lane.
          setElementLanes(currentLanes => {
            const draggingElement = currentLanes.flat().find(el => el.id === elementId);
            if (!draggingElement) {
              return currentLanes;
            }

            const updatedElement = updateElementFrames({
              element: draggingElement,
              dragOffset: timeOffset,
              dragSide: ElementResizeSideEnum.CENTER,
              initialFrames: { fromFrame: draggingElement.fromFrame, toFrame: draggingElement.toFrame },
              fps
            });

            const updatedLanes = updateElementLanesOnHorizontalDrag(currentLanes, updatedElement);
            elementLanesRef.current = updatedLanes;

            if (playerConfig) {
              updatePlayerConfigWithElementLanes(playerConfig, updatedLanes, clipData, clipId);
            }

            return updatedLanes;
          });
        }
      } else if (data) {
        // Pure horizontal drag (or resize): commit the snapped frame offset once.
        const { elementId, side } = data;

        setElementLanes(currentLanes => {
          const draggingElement = currentLanes.flat().find(el => el.id === elementId);
          if (!draggingElement) {
            return currentLanes;
          }

          const updatedElement = updateElementFrames({
            element: draggingElement,
            dragOffset: timeOffset,
            dragSide: side,
            initialFrames: { fromFrame: draggingElement.fromFrame, toFrame: draggingElement.toFrame },
            fps
          });

          const updatedLanes = updateElementLanesOnHorizontalDrag(currentLanes, updatedElement);
          elementLanesRef.current = updatedLanes;

          if (playerConfig) {
            updatePlayerConfigWithElementLanes(playerConfig, updatedLanes, clipData, clipId);
          }

          return updatedLanes;
        });
      }

      snappedDeltaXRef.current = 0;
      initialFramesRef.current = null;
      elementLanesRef.current = null;
      isVerticalDragModeRef.current = false;
      resetDragState();
    },
    [
      fps,
      playerConfig,
      clipData,
      clipId,
      elementLanesRef,
      initialFramesRef,
      isUpdatingFromVerticalDragRef,
      setElementLanes,
      resetDragState
    ]
  );

  const handleDragCancel = useCallback(
    (_event: DragCancelEvent) => {
      document.body.style.cursor = '';
      EventBus.dispatch(CustomEvents.NewTimelineEntityDraggingStateChanged, { isDraggingTimelineEntity: false });
      snappedDeltaXRef.current = 0;
      isVerticalDragModeRef.current = false;
      resetDragState();
    },
    [resetDragState]
  );

  return { sensors, snapModifier, handleDragStart, handleDragMove, handleDragEnd, handleDragCancel };
};
